import { apiFetch } from './http';

/** One of the panel's match reports as the platform mirrors it (docs/design/ASM_INTEGRATION_STUDY.md, 3.4). */
export interface ReportView {
  id: number;
  panelServerId: number;
  panelMatchId: number;
  code: string | null;
  serverName: string | null;
  /** The panel's own name of the mission: a file name (`ops_2xqvgqb`) for one the platform published. */
  missionName: string | null;
  /**
   * What a person reads instead: the platform mission's title, the panel's name
   * for a hand-made mission, "Partida a <terrain>" for one of the platform's
   * that is gone. Absent from older platforms.
   */
  displayTitle?: string | null;
  world: string | null;
  status: string | null;
  startedAt: string | null;
  endedAt: string | null;
  durationSeconds: number;
  playerCount: number;
  totalKills: number;
  totalFriendlyKills: number;
  endReason: string | null;
  reportUrl: string | null;
  pdfUrl: string | null;
  rosterSynced: boolean;
  /** This account took part, by the Steam identity on its roster; absent from older platforms. */
  played?: boolean;
}

export interface ReportsList {
  reports: ReportView[];
  canManage: boolean;
  syncedAt: string | null;
}

export const fetchReports = () => apiFetch<ReportsList>('/api/reports');

/** Who fired one weapon, most of it first. */
export interface WeaponUser {
  name: string;
  shots: number;
  kills: number;
}

export interface MatchWeapon {
  className: string;
  name: string;
  shots: number;
  kills: number;
  users: WeaponUser[];
}

export interface MatchVehicle {
  className: string;
  name: string;
  /** Granular to the panel's sampling interval, not exact. */
  secondsUsed: number;
  distanceMeters: number;
  kills: number;
}

export interface MatchDestroyed {
  className: string;
  name: string;
  side: string;
  destroyed: number;
  friendlyLosses: number;
  /** Empty when the AI did it. */
  destroyers: string[];
}

/**
 * What was fired and what was driven, out of the panel's own report.
 *
 * The panel has counted all of this since August and this platform showed four
 * numbers and a link out to it. Nothing is recomputed: the classes come named,
 * so the rows can ask `/api/catalog/picture` for the icons the panel also
 * already has. 404 for a match the panel has no report for, which is ordinary.
 */
/** One task, as the engine ended it. */
export interface MatchTask {
  title: string;
  description: string;
  /** CREATED · ASSIGNED · SUCCEEDED · FAILED · CANCELED. */
  state: string;
  x: number;
  y: number;
}

/**
 * One kill. Vehicles, scenery and animals travel through the same feed as men,
 * which is what `victimKind` is for; the time is the clock's, not the mission's.
 */
export interface MatchKill {
  at: string;
  killer: string;
  victim: string;
  killerIsPlayer: boolean;
  victimIsPlayer: boolean;
  friendlyFire: boolean;
  suicide: boolean;
  killerSide: string;
  victimSide: string;
  victimKind: string;
  weaponClass: string;
  weaponName: string;
  distance: number;
}

/**
 * One square of a heat map, in terrain metres.
 *
 * The panel draws these over its own picture of the world; this platform draws
 * them over the mission they were played from, with the objectives on it, which
 * is the thing the panel cannot do — it does not know what the objectives were.
 */
export interface HeatPoint {
  x: number;
  y: number;
  weight: number;
}

export interface MatchDetail {
  code: string;
  weapons: MatchWeapon[];
  vehicles: MatchVehicle[];
  destroyed: MatchDestroyed[];
  tasks: MatchTask[];
  killFeed: MatchKill[];
  deathHeat: HeatPoint[];
  movementHeat: HeatPoint[];
}

export const fetchReportDetail = (id: number) => apiFetch<MatchDetail>(`/api/reports/${id}/detail`);

/**
 * The same, for the session that was played. 404 while the night is not over,
 * or when the panel has no report for it, which is how the page knows not to
 * show the section at all.
 */
export const fetchSessionAftermath = (code: string) =>
  apiFetch<MatchDetail>(`/api/sessions/${code}/aftermath`);
export const syncReports = () =>
  apiFetch<{ matches: number; rostersFilled: number; rosterEndpointMissing: boolean }>(
    '/api/reports/sync',
    { method: 'POST' },
  );
export const deleteReport = (id: number) =>
  apiFetch<void>(`/api/reports/${id}`, { method: 'DELETE' });
export const regenerateReport = (id: number) =>
  apiFetch<void>(`/api/reports/${id}/regenerate`, { method: 'POST' });
